import { useEffect, useRef } from "react";
import { Atomic, Product } from "@atomicfi/transact-javascript";
import { useInstitution } from "byzantine/src/dbbl/businessLogic/entities";

type AtomicTransactionHandle = {
  close: () => void;
};

export type AtomicModalProps = {
  open?: boolean;
  linkToken?: string;
  onSuccess?: () => void;
  onExit?: () => void;
};

const AtomicModal = ({ open = false, linkToken, onSuccess, onExit }: AtomicModalProps) => {
  const institution = useInstitution();
  const transactionRef = useRef<AtomicTransactionHandle | null>(null);
  const finishedRef = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  const onExitRef = useRef(onExit);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  useEffect(() => {
    onExitRef.current = onExit;
  }, [onExit]);

  useEffect(() => {
    if (!open) {
      if (transactionRef.current) {
        transactionRef.current.close();
        transactionRef.current = null;
      }
      finishedRef.current = false;
      return undefined;
    }

    if (transactionRef.current || !linkToken) {
      return undefined;
    }

    transactionRef.current = Atomic.transact({
      config: {
        publicToken: linkToken,
        scope: "user-link",
        tasks: [{ operation: Product.DEPOSIT }],
        theme: {
          brandColor: institution?.primary_color_hex || "#1A4338",
          overlayColor: "#f9f9f9",
          dark: false,
          navigationOptions: {
            showBackButton: true,
            showBackButtonText: false,
            showCloseButton: true,
          },
        },
        language: "en",
      },
      onFinish: () => {
        finishedRef.current = true;
        transactionRef.current = null;
        onSuccessRef.current?.();
        onExitRef.current?.();
      },
      onClose: () => {
        transactionRef.current = null;
        if (!finishedRef.current) {
          onExitRef.current?.();
        }
        finishedRef.current = false;
      },
    });

    return () => {
      transactionRef.current?.close();
      transactionRef.current = null;
      finishedRef.current = false;
    };
  }, [open, linkToken, institution?.primary_color_hex]);

  return null;
};

export default AtomicModal;
